import { EquipmentInfoSearchInputs } from "../../pages/setting/EquipmentInfoPage";
import { ChangeEvent, useState } from "react";
import { SelectOption } from "../types/selectOption";
import { useQuery } from "@tanstack/react-query";
import { request } from "../../common/api";
import {
  GET_LOW_MACHINERY_SELECT_OPTIONS,
  GET_MACHINERY_SELECT_OPTIONS,
  GET_SENSOR_SELECT_OPTIONS,
  GET_SHIP_SELECT_OPTIONS,
  GET_SHIP_TYPE_SELECT_OPTIONS,
} from "../query-keys";

const useEquipmentInfoSearchInputData = (
  searchDefaultValues: EquipmentInfoSearchInputs
) => {
  const [search, setSearch] =
    useState<EquipmentInfoSearchInputs>(searchDefaultValues);

  // 함형 options 조회
  const { data: shipTypeSelectOptions } = useQuery<SelectOption[]>({
    queryKey: [GET_SHIP_TYPE_SELECT_OPTIONS, "all"],
    queryFn: async () => {
      const result = await request<SelectOption[]>({
        method: "GET",
        url: "/equipment-code/select/options",
      });

      return [{ label: "전체", value: "" }, ...result];
    },
  });

  // 함정 options 조회
  const { data: shipSelectOptions } = useQuery<SelectOption[]>({
    queryKey: [GET_SHIP_SELECT_OPTIONS, search.ssrShipTypeNo],
    queryFn: async () => {
      if (!search.ssrShipTypeNo) {
        return [];
      }

      return await request<SelectOption[]>({
        method: "GET",
        url: `/ship-info/select/options/${search.ssrShipTypeNo}`,
      });
    },
  });

  // 장비 options 조회
  const { data: machinerySelectOptions } = useQuery<SelectOption[]>({
    queryKey: [GET_MACHINERY_SELECT_OPTIONS, search.ssrShipNo],
    queryFn: async () => {
      if (!search.ssrShipNo) {
        return [];
      }

      return await request<SelectOption[]>({
        method: "GET",
        url: `/equipment-info/machinery/select/options/${search.ssrShipNo}`,
      });
    },
  });

  // 하위장비 options 조회
  const { data: lowMachinerySelectOptions } = useQuery<SelectOption[]>({
    queryKey: [GET_LOW_MACHINERY_SELECT_OPTIONS, search.ssrMachine],
    queryFn: async () => {
      if (!search.ssrMachine) {
        return [];
      }

      return await request<SelectOption[]>({
        method: "GET",
        url: `/equipment-info/low-machinery/select/options/${search.ssrMachine}`,
      });
    },
  });

  // 센서 options 조회
  const { data: sensorSelectOptions } = useQuery<SelectOption[]>({
    queryKey: [GET_SENSOR_SELECT_OPTIONS, search.ssrLowMcyName],
    queryFn: async () => {
      if (!search.ssrLowMcyName) {
        return [];
      }

      return await request<SelectOption[]>({
        method: "GET",
        url: `/sensor/select/options`,
        params: {
          ssrShipNo: search.ssrShipNo,
          ssrMcyName: search.ssrMcyName,
          ssrLowMcyName: search.ssrLowMcyName,
        },
      });
    },
  });

  // 함형 선택 변경
  const handleShipTypeSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSearch({
      ssrShipTypeNo: e.target.value,
    });
  };

  // 함정 선택 변경
  const handleShipSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSearch({
      ssrShipTypeNo: search.ssrShipTypeNo,
      ssrShipNo: e.target.value,
    });
  };

  // 장비 선택 변경
  const handleMcySelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSearch({
      ssrShipTypeNo: search.ssrShipTypeNo,
      ssrShipNo: search.ssrShipNo,
      ssrMcyName: e.target.options[e.target.selectedIndex].text,
      ssrMachine: e.target.value,
    });
  };

  // 하위장비 선택 변경
  const handleLowMcySelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSearch({
      ...search,
      ssrLowMcyName: e.target.value,
    });
  };

  // 센서 값 및 날짜 변경
  const handleValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setSearch({
      ...search,
      [name]: value,
    });
  };

  return {
    search,

    /** 함형 select options */
    shipTypeSelectOptions,

    /** 함정 select options */
    shipSelectOptions,

    /** 장비 select options */
    machinerySelectOptions,

    /** 하위장비 select options */
    lowMachinerySelectOptions,

    /** 센서 select options */
    sensorSelectOptions,

    /** 함형 선택 변경 */
    handleShipTypeSelectChange,

    /** 함정 선택 변경 */
    handleShipSelectChange,

    /** 장비 선택 변경 */
    handleMcySelectChange,

    /** 하위장비 선택 변경 */
    handleLowMcySelectChange,

    /** 센서 값 및 날짜 변경*/
    handleValueChange,
  };
};

export default useEquipmentInfoSearchInputData;
